import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/constants";

function ProductTag({ product }) {
  const isTG = product === "testguard";
  return (
    <span
      data-testid={`product-tag-${product || "campuspath"}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-700 ${
        isTG ? "bg-[#0B1526] text-leaf" : "bg-[#0a3a66]/10 text-[#0a3a66]"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${isTG ? "bg-leaf" : "bg-[#0a3a66]"}`} />
      {isTG ? "TestGuard" : "CampusPath"}
    </span>
  );
}

function ProductFilter({ value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-product-filter`}>
      {[["all", "All products"], ["campuspath", "CampusPath"], ["testguard", "TestGuard"]].map(([k, label]) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          data-testid={`${testidPrefix}-product-${k}`}
          className={`rounded-full px-4 py-1.5 text-xs font-700 transition-colors duration-200 ${
            value === k ? "bg-ink text-white" : "border border-ink/15 text-ink/60 hover:border-ink/40"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian px-6 text-white dark-scroll">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-[2rem] border border-white/10 bg-surface-dark p-10"
      >
        <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm font-600 text-white/50 transition-colors hover:text-leaf" data-testid="admin-back-home">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          Back to CampusPath
        </Link>
        <div className="flex items-center gap-2.5">
          <img src={LOGO} alt="CampusPath" className="h-16 w-auto object-contain" />
          <span className="font-display text-xl font-800">Campus<span className="text-leaf">Path</span></span>
        </div>
        <h1 className="mt-8 font-display text-3xl font-800">Team Login</h1>
        <p className="mt-2 text-sm text-white/50">Access the leads dashboard.</p>
        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-login-form">
          <div>
            <label className="mb-2 block text-[11px] font-600 uppercase tracking-widest text-white/40">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-email"
              className="w-full border-b border-white/20 bg-transparent pb-2.5 outline-none transition-colors focus:border-leaf"
              placeholder="admin@campuspath.org"
            />
          </div>
          <div>
            <label className="mb-2 block text-[11px] font-600 uppercase tracking-widest text-white/40">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password"
              className="w-full border-b border-white/20 bg-transparent pb-2.5 outline-none transition-colors focus:border-leaf"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full rounded-full bg-leaf py-3.5 text-sm font-700 text-obsidian transition-[background-color,transform] duration-300 hover:bg-white active:scale-[0.98] disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, trials: 0, demos: 0 });
  const [filter, setFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("leads");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { lead_type: filter };
      const [l, s] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/stats"),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const matchesProduct = (p) => productFilter === "all" || (p || "campuspath") === productFilter;
  const visibleLeads = leads.filter((l) => matchesProduct(l.product));

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-30 border-b border-ink/10 bg-bone/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-8">
          <div className="flex items-center gap-2.5">
            <img src={LOGO} alt="CampusPath" className="h-11 w-auto object-contain" />
            <span className="font-display text-lg font-800 text-ink">Leads Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-ink/50 sm:inline">{user?.email}</span>
            <button onClick={() => navigate("/")} className="text-sm font-600 text-ink/60 transition-colors hover:text-ink" data-testid="admin-view-site">View site</button>
            <button onClick={logout} data-testid="admin-logout-btn" className="rounded-full border border-ink/20 px-4 py-2 text-sm font-600 text-ink transition-colors hover:border-ink hover:bg-ink hover:text-white">
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-8">
        <div className="mb-8 flex gap-2" data-testid="admin-tabs">
          {[["leads", "Leads"], ["bookings", "Bookings"], ["downloads", "Downloads"], ["team", "Team"], ["settings", "Settings"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`rounded-full px-5 py-2 text-sm font-600 transition-colors duration-200 ${
                tab === k ? "bg-ink text-white" : "border border-ink/15 text-ink/60 hover:border-ink/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "leads" && (
        <>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            ["Total leads", stats.total, "all"],
            ["Trial requests", stats.trials, "trial"],
            ["Demo requests", stats.demos, "demo"],
          ].map(([label, value, key]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`stat-${key}`}
              className={`rounded-2xl border p-6 text-left transition-colors duration-200 ${
                filter === key ? "border-leaf bg-white" : "border-ink/10 bg-white/60 hover:border-ink/30"
              }`}
            >
              <div className="font-display text-4xl font-900 tracking-tightest text-ink">{value}</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-ink/50">{label}</div>
            </button>
          ))}
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-ink/10 bg-white">
          <div className="flex flex-col gap-3 border-b border-ink/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-display text-lg font-800 text-ink">
              {filter === "all" ? "All leads" : filter === "trial" ? "Trial requests" : "Demo requests"}
            </h2>
            <div className="flex items-center gap-4">
              <ProductFilter value={productFilter} onChange={setProductFilter} testidPrefix="leads" />
              <button onClick={load} className="text-sm font-600 text-leaf-600 hover:underline" data-testid="admin-refresh">Refresh</button>
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-ink/40">Loading…</div>
          ) : visibleLeads.length === 0 ? (
            <div className="p-16 text-center text-ink/40" data-testid="admin-empty">No leads yet. They'll appear here as soon as someone signs up.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-ink/10 text-[11px] uppercase tracking-widest text-ink/40">
                    <th className="px-6 py-3 font-600">Name</th>
                    <th className="px-4 py-3 font-600">Type</th>
                    <th className="px-4 py-3 font-600">Source</th>
                    <th className="px-4 py-3 font-600">School</th>
                    <th className="px-4 py-3 font-600">Email</th>
                    <th className="px-4 py-3 font-600">Detail</th>
                    <th className="px-6 py-3 font-600">When</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((l) => (
                    <tr key={l.id} className="border-b border-ink/5 transition-colors hover:bg-bone" data-testid={`lead-row-${l.id}`}>
                      <td className="px-6 py-4 font-600 text-ink">{l.first_name} {l.last_name}<div className="text-xs font-400 text-ink/40">{l.role || "—"}</div></td>
                      <td className="px-4 py-4">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-700 ${l.lead_type === "trial" ? "bg-leaf/15 text-leaf-600" : "bg-ink/10 text-ink"}`}>
                          {l.lead_type}
                        </span>
                      </td>
                      <td className="px-4 py-4"><ProductTag product={l.product} /></td>
                      <td className="px-4 py-4 text-ink/80">{l.school_name}<div className="text-xs text-ink/40">{l.location || ""}</div></td>
                      <td className="px-4 py-4 text-ink/70">
                        <a href={`mailto:${l.email}`} className="hover:text-leaf-600">{l.email}</a>
                        <div className="text-xs text-ink/40">{l.phone || ""}</div>
                      </td>
                      <td className="px-4 py-4 text-ink/70">
                        {l.lead_type === "trial"
                          ? <span className="font-600">{l.trial_days} days</span>
                          : <span>{[l.preferred_date, l.preferred_time].filter(Boolean).join(" · ") || "—"}</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-ink/50">{fmtDate(l.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {tab === "bookings" && <BookingsPanel />}
        {tab === "downloads" && <DownloadsPanel />}
        {tab === "team" && <TeamPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}

function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productFilter, setProductFilter] = useState("all");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/bookings");
      setBookings(r.data);
    } catch {
      toast.error("Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const fmtTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    const hh = ((h + 11) % 12) + 1;
    return `${hh}:${m.toString().padStart(2, "0")} ${ap}`;
  };
  const visibleBookings = bookings.filter((b) => productFilter === "all" || (b.product || "campuspath") === productFilter);
  return (
    <div className="overflow-hidden rounded-2xl border border-ink/10 bg-white" data-testid="bookings-panel">
      <div className="flex flex-col gap-3 border-b border-ink/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-display text-lg font-800 text-ink">Demo bookings</h2>
        <div className="flex items-center gap-4">
          <ProductFilter value={productFilter} onChange={setProductFilter} testidPrefix="bookings" />
          <button onClick={load} className="text-sm font-600 text-leaf-600 hover:underline" data-testid="bookings-refresh">Refresh</button>
        </div>
      </div>
      {loading ? (
        <div className="p-10 text-center text-ink/40">Loading…</div>
      ) : visibleBookings.length === 0 ? (
        <div className="p-16 text-center text-ink/40" data-testid="bookings-empty">No demo bookings yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="bookings-table">
            <thead>
              <tr className="border-b border-ink/10 text-[11px] uppercase tracking-widest text-ink/40">
                <th className="px-6 py-3 font-600">Date</th>
                <th className="px-4 py-3 font-600">Time (ET)</th>
                <th className="px-4 py-3 font-600">Name</th>
                <th className="px-4 py-3 font-600">School</th>
                <th className="px-4 py-3 font-600">Email</th>
                <th className="px-6 py-3 font-600">Source</th>
              </tr>
            </thead>
            <tbody>
              {visibleBookings.map((b) => (
                <tr key={b.id} className="border-b border-ink/5 transition-colors hover:bg-bone" data-testid={`booking-row-${b.id}`}>
                  <td className="px-6 py-4 font-600 text-ink">{b.date}</td>
                  <td className="px-4 py-4 text-ink/80">{fmtTime(b.time)}</td>
                  <td className="px-4 py-4 text-ink/80">{b.first_name} {b.last_name}</td>
                  <td className="px-4 py-4 text-ink/70">{b.school_name}</td>
                  <td className="px-4 py-4 text-ink/70"><a href={`mailto:${b.email}`} className="hover:text-leaf-600">{b.email}</a></td>
                  <td className="px-6 py-4"><ProductTag product={b.product} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SettingsPanel() {
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setEmails(r.data.notify_emails || []))
      .catch(() => toast.error("Failed to load settings."))
      .finally(() => setLoading(false));
  }, []);

  const addEmail = () => {
    const e = input.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Enter a valid email address."); return; }
    if (emails.includes(e)) { toast.error("That email is already added."); return; }
    setEmails((x) => [...x, e]);
    setInput("");
  };
  const removeEmail = (e) => setEmails((x) => x.filter((y) => y !== e));

  const save = async () => {
    if (emails.length === 0) { toast.error("Add at least one recipient."); return; }
    setSaving(true);
    try {
      const r = await api.put("/settings", { notify_emails: emails });
      setEmails(r.data.notify_emails);
      toast.success("Recipients saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl rounded-2xl border border-ink/10 bg-white p-8" data-testid="settings-panel">
      <h2 className="font-display text-lg font-800 text-ink">Notification recipients</h2>
      <p className="mt-2 text-sm text-ink/50">These addresses receive every new trial/lead submission and demo booking.</p>
      {loading ? (
        <div className="mt-6 text-ink/40">Loading…</div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="recipient-list">
            {emails.map((e) => (
              <span key={e} className="inline-flex items-center gap-2 rounded-full bg-leaf/10 px-3 py-1.5 text-sm font-600 text-ink" data-testid={`recipient-${e}`}>
                {e}
                <button onClick={() => removeEmail(e)} data-testid={`remove-${e}`} className="text-lg leading-none text-ink/40 hover:text-ink" aria-label="Remove">×</button>
              </span>
            ))}
            {emails.length === 0 && <span className="text-sm text-ink/40">No recipients yet.</span>}
          </div>
          <div className="mt-5 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
              data-testid="recipient-input"
              placeholder="name@school.org"
              className="flex-1 rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none transition-colors focus:border-leaf"
            />
            <button onClick={addEmail} data-testid="recipient-add" className="rounded-xl border border-ink/20 px-4 py-2.5 text-sm font-600 text-ink transition-colors hover:border-ink">Add</button>
          </div>
          <button onClick={save} disabled={saving} data-testid="settings-save" className="mt-6 rounded-full bg-leaf px-6 py-3 text-sm font-700 text-ink transition-colors duration-300 hover:bg-ink hover:text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save recipients"}
          </button>
        </>
      )}
    </div>
  );
}

function DownloadsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("url");
  const [form, setForm] = useState({ label: "", product: "campuspath", category: "package", url: "", size_label: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/downloads"); setItems(r.data); }
    catch { toast.error("Failed to load downloads."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reset = () => { setForm({ label: "", product: "campuspath", category: "package", url: "", size_label: "" }); setFile(null); };

  const addUrl = async () => {
    if (!form.label.trim() || !form.url.trim()) { toast.error("Label and URL are required."); return; }
    setBusy(true);
    try {
      await api.post("/downloads", { label: form.label, product: form.product, category: form.category, url: form.url, size_label: form.size_label || null });
      toast.success("Download link added.");
      reset(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  const uploadFile = async () => {
    if (!form.label.trim() || !file) { toast.error("Label and a file are required."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", form.label);
    fd.append("product", form.product);
    fd.append("category", form.category);
    if (form.size_label) fd.append("size_label", form.size_label);
    setBusy(true);
    try {
      await api.post("/downloads/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded.");
      reset(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed."); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/downloads/${id}`); setItems((x) => x.filter((i) => i.id !== id)); toast.success("Removed."); }
    catch { toast.error("Failed to remove."); }
  };

  const inputCls = "rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none transition-colors focus:border-leaf";

  return (
    <div className="space-y-6" data-testid="downloads-panel">
      <div className="rounded-2xl border border-ink/10 bg-white p-6">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setMode("url")} data-testid="dl-mode-url" className={`rounded-full px-4 py-1.5 text-sm font-600 transition-colors ${mode === "url" ? "bg-ink text-white" : "border border-ink/15 text-ink/60"}`}>Link (external / large files)</button>
          <button onClick={() => setMode("upload")} data-testid="dl-mode-upload" className={`rounded-full px-4 py-1.5 text-sm font-600 transition-colors ${mode === "upload" ? "bg-ink text-white" : "border border-ink/15 text-ink/60"}`}>Upload file</button>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <input value={form.label} onChange={set("label")} data-testid="dl-label" placeholder="Label (e.g. CampusPath Appliance)" className={inputCls} />
          <select value={form.product} onChange={set("product")} data-testid="dl-product" className={inputCls}>
            <option value="campuspath">CampusPath</option>
            <option value="testguard">TestGuard</option>
            <option value="both">Both</option>
          </select>
          <select value={form.category} onChange={set("category")} data-testid="dl-category" className={inputCls}>
            <option value="appliance">Appliance</option>
            <option value="package">Package</option>
            <option value="other">Other</option>
          </select>
          <input value={form.size_label} onChange={set("size_label")} data-testid="dl-size" placeholder="Size label (optional, e.g. 3.0 GB)" className={inputCls} />
          {mode === "url" ? (
            <input value={form.url} onChange={set("url")} data-testid="dl-url" placeholder="https://…/appliance.ova" className={`sm:col-span-2 ${inputCls}`} />
          ) : (
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="dl-file" className="sm:col-span-2 text-sm text-ink/70" />
          )}
        </div>
        {mode === "upload" && (
          <p className="mt-3 text-xs text-ink/40">Tip: for large files like the 3&nbsp;GB appliance, use the “Link” option and host the file externally (e.g. your bucket or itlocalbiz.com). Uploads are best for smaller packages (≈ up to 50&nbsp;MB).</p>
        )}
        <button onClick={mode === "url" ? addUrl : uploadFile} disabled={busy} data-testid="dl-submit" className="mt-5 rounded-full bg-leaf px-6 py-3 text-sm font-700 text-ink transition-colors duration-300 hover:bg-ink hover:text-white disabled:opacity-60">
          {busy ? "Saving…" : mode === "url" ? "Add link" : "Upload file"}
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-ink/10 bg-white">
        <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
          <h2 className="font-display text-lg font-800 text-ink">Trial downloads</h2>
          <button onClick={load} className="text-sm font-600 text-leaf-600 hover:underline" data-testid="downloads-refresh">Refresh</button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-ink/40">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-ink/40" data-testid="downloads-empty">No downloads yet. Add a link or upload a file above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="downloads-table">
              <thead>
                <tr className="border-b border-ink/10 text-[11px] uppercase tracking-widest text-ink/40">
                  <th className="px-6 py-3 font-600">Label</th>
                  <th className="px-4 py-3 font-600">Product</th>
                  <th className="px-4 py-3 font-600">Category</th>
                  <th className="px-4 py-3 font-600">Type</th>
                  <th className="px-4 py-3 font-600">Size</th>
                  <th className="px-6 py-3 font-600"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-b border-ink/5" data-testid={`download-row-${d.id}`}>
                    <td className="px-6 py-4 font-600 text-ink"><a href={d.download_url} target="_blank" rel="noreferrer" className="hover:text-leaf-600">{d.label}</a></td>
                    <td className="px-4 py-4 text-ink/70">{d.product}</td>
                    <td className="px-4 py-4 text-ink/70">{d.category}</td>
                    <td className="px-4 py-4 text-ink/70">{d.source_type}</td>
                    <td className="px-4 py-4 text-ink/70">{d.size_label || "—"}</td>
                    <td className="px-6 py-4 text-right"><button onClick={() => remove(d.id)} data-testid={`dl-remove-${d.id}`} className="text-sm font-600 text-[#e5484d] hover:underline">Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/users"); setUsers(r.data); }
    catch { toast.error("Failed to load team."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addUser = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error("Name, email and an 8+ character password are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users", { name: form.name, email: form.email, password: form.password });
      toast.success("Team member added.");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add member."); }
    finally { setBusy(false); }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((x) => x.filter((y) => y.id !== u.id));
      toast.success("Removed.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove."); }
  };

  const inputCls = "rounded-xl border border-ink/15 px-4 py-2.5 text-sm outline-none transition-colors focus:border-leaf";

  return (
    <div className="space-y-6" data-testid="team-panel">
      <form onSubmit={addUser} className="rounded-2xl border border-ink/10 bg-white p-6">
        <h2 className="font-display text-lg font-800 text-ink">Add a team member</h2>
        <p className="mt-2 text-sm text-ink/50">New members can sign in to this dashboard with full access.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <input value={form.name} onChange={set("name")} data-testid="user-name" placeholder="Full name" className={inputCls} />
          <input value={form.email} onChange={set("email")} type="email" data-testid="user-email" placeholder="name@campuspath.org" className={inputCls} />
          <input value={form.password} onChange={set("password")} type="password" data-testid="user-password" placeholder="Password (8+ chars)" className={inputCls} />
        </div>
        <button type="submit" disabled={busy} data-testid="user-add-btn" className="mt-5 rounded-full bg-leaf px-6 py-3 text-sm font-700 text-ink transition-colors duration-300 hover:bg-ink hover:text-white disabled:opacity-60">
          {busy ? "Adding…" : "Add team member"}
        </button>
      </form>

      <div className="overflow-hidden rounded-2xl border border-ink/10 bg-white">
        <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
          <h2 className="font-display text-lg font-800 text-ink">Team accounts</h2>
          <button onClick={load} className="text-sm font-600 text-leaf-600 hover:underline" data-testid="team-refresh">Refresh</button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-ink/40">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="team-table">
              <thead>
                <tr className="border-b border-ink/10 text-[11px] uppercase tracking-widest text-ink/40">
                  <th className="px-6 py-3 font-600">Name</th>
                  <th className="px-4 py-3 font-600">Email</th>
                  <th className="px-4 py-3 font-600">Role</th>
                  <th className="px-6 py-3 font-600"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-ink/5" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4 font-600 text-ink">{u.name || "—"}</td>
                    <td className="px-4 py-4 text-ink/70">{u.email}</td>
                    <td className="px-4 py-4"><span className="rounded-full bg-ink/10 px-2.5 py-1 text-xs font-700 text-ink">{u.role}</span></td>
                    <td className="px-6 py-4 text-right">
                      {user && user.email === u.email ? (
                        <span className="text-xs text-ink/40">You</span>
                      ) : (
                        <button onClick={() => removeUser(u)} data-testid={`user-remove-${u.id}`} className="text-sm font-600 text-[#e5484d] hover:underline">Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex min-h-screen items-center justify-center bg-obsidian text-white/50">Loading…</div>;
  return user ? <Dashboard /> : <Login />;
}
